import {useCallback, useContext, useEffect, useMemo, useState} from 'react';

import {SvgIcon, Typography} from '@mui/material';
import {Button, Spinning, Stack} from '@zoom/react-ui';
import {format} from 'date-fns';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';

import BookingFormContext from './BookingFormContext';

import {ReactComponent as calendarIllustration} from 'assets/svg/calendar-illustration.svg';
// import {toDateTime} from 'Components/BookingBlocksWidget/utils';
import {fetchBillingOrderInfoExternalThunk} from 'Store/OrganizationSettingStore';
import {formatPriceTo2Decimal} from 'Utils';
import {CURRENCY_SYMBOL} from 'Utils/consts';

const titleSx = {
  fontSize: '1rem',
  fontWeight: 600,
  lineHeight: '1.25rem',
  color: '#2A2B2D',
};

const descSx = (theme) => ({
  fontSize: '0.875rem',
  fontWeight: 400,
  lineHeight: '1.125rem',
  color: theme.palette.text.neutral,
});

const detailSx = (theme) => ({
  border: `1px solid ${theme.palette.common.swatch[30]}`,
  borderRadius: '.625rem',
  padding: '.75rem',
  marginTop: '2rem',
  gap: '.75rem',
});

const totalSx = (theme) => ({
  fontSize: '1rem',
  fontWeight: 600,
  lineHeight: '1.25rem',
  color: theme.palette.text.neutral,
});

const itemSx = (theme) => ({
  fontSize: '0.75rem',
  fontWeight: 400,
  lineHeight: '1.125rem',
  color: theme.palette.text.strongerNeutral,
  maxWidth: '16rem',
  textAlign: 'right',
  wordBreak: 'break-all',
});

const PayButBookingError = ({appointment, bookingError, calendarId, timeZone}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const {
    bizOrderId,
  } = useContext(BookingFormContext);

  const apptId = appointment?.id;
  const price = appointment?.scheduleCost;
  const currencySymbol = CURRENCY_SYMBOL[appointment?.currency];

  const [orderInfo, setOrderInfo] = useState({});
  const [loading, setLoading] = useState(true);

  const {createTime, displayName, email, exclusiveTax, thirdOrderId, totalFee} = orderInfo;

  const paidOn = createTime ? format(new Date(createTime), 'yyyy/MM/dd - hh:mm:ss') : '';

  const errorTips = useMemo(() => {
    const errMsg = (bookingError?.body?.error?.message) || '';
    if (bookingError?.errorCode === 400 && errMsg.includes('Requested time slot busy')) {
      return t('payment.reason_busy');
    }
    // show the booking page is inactive or deleted dialog if the booking page is inactive or deleted
    if (bookingError?.errorCode === 400 &&
      (errMsg.includes('Booking page is inactive') ||
      errMsg.includes('Booking page has been deleted') ||
      errMsg.includes('Not found')) ) {
      return t('payment.reason_deactivated');
    }
    return t('payment.reason_error');
  }, [bookingError, t]);

  useEffect(() => {
    (async () => {
      if (bizOrderId && apptId && calendarId) {
        setLoading(true);
        const response = await dispatch(fetchBillingOrderInfoExternalThunk({
          user: calendarId,
          bizOrderId: bizOrderId,
          apptId: apptId,
        })).unwrap();
        setOrderInfo(response);
      }
      setLoading(false);
    })();
  }, [bizOrderId, calendarId, apptId, dispatch]);

  const handleOnContactHost = useCallback(() => {
    const link = document.createElement('a');

    link.href = `mailto: ${encodeURIComponent(email)}`;

    link.style.display = 'none';
    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
  }, [email]);

  return (
    <Stack alignItems="center" justifyContent="center">
      <Stack width="27.5rem" alignItems="center">
        <SvgIcon
          style={{width: '12.5rem', height: '12.5rem'}}
          component={calendarIllustration}
          fontSize="inherit"
          inheritViewBox
        />
        {loading && (
          <Stack
            alignItems="center"
            justifyContent="center"
            width="100%"
            height="31.5rem"
          >
            <Spinning size="large" />
          </Stack>
        )}
        {!loading && (
          <>
            <Typography sx={titleSx} marginTop="1rem" textAlign="center">{t('payment.payAndBookError')}</Typography>
            <Typography sx={descSx} textAlign="center">
              {t('payment.payAndBookErrorDesc', {reason: errorTips, hostName: displayName})}
            </Typography>

            <Stack width="100%" sx={detailSx}>
              <Typography sx={titleSx}>{t('payment.paymentDetails')}</Typography>

              <Stack direction="row" justifyContent="space-between">
                <Typography sx={descSx}>{t('payment.hostName')}</Typography>
                <Typography sx={itemSx}>{displayName}</Typography>
              </Stack>

              <Stack direction="row" justifyContent="space-between">
                <Typography sx={descSx}>{t('payment.hostEmail')}</Typography>
                <Typography sx={itemSx}>{email}</Typography>
              </Stack>

              <Stack direction="row" justifyContent="space-between">
                <Typography sx={descSx}>{t('payment.paidOn')}</Typography>
                <Typography sx={itemSx}>{paidOn}</Typography>
              </Stack>

              <Stack direction="row" justifyContent="space-between">
                <Typography sx={descSx}>{t('payment.transactionId')}</Typography>
                <Typography sx={itemSx}>{thirdOrderId}</Typography>
              </Stack>

              {exclusiveTax > 0 && (
                <>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography sx={descSx}>{t('payment.price')}</Typography>
                    <Typography sx={itemSx}>{currencySymbol + formatPriceTo2Decimal(price)}</Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography sx={descSx}>{t('payment.taxes')}</Typography>
                    <Typography sx={itemSx}>{currencySymbol + formatPriceTo2Decimal(exclusiveTax)}</Typography>
                  </Stack>
                </>
              )}

              <Stack direction="row" justifyContent="space-between">
                <Typography sx={totalSx}>{t('payment.total')}</Typography>
                <Typography>{currencySymbol + formatPriceTo2Decimal(totalFee)}</Typography>
              </Stack>
            </Stack>
            <Button sx={{marginTop: '2rem'}} type="primary" onClick={handleOnContactHost}>
              {t('payment.contactHost')}
            </Button>
          </>
        )}
      </Stack>
    </Stack>
  );
};

export default PayButBookingError;
